// src/components/user/controltower/ExpressDeliveryModal.jsx
import React, { useEffect, useRef } from 'react';
import { X, MapPin, Navigation, Phone, User, Package, Zap, DollarSign } from 'lucide-react';

const STATUS_STYLES = {
  pending:   'bg-orange-100 text-orange-700 border-orange-300',
  encamino:  'bg-blue-100 text-blue-700 border-blue-300',
  entregado: 'bg-green-100 text-green-700 border-green-300',
  cancelado: 'bg-red-100 text-red-700 border-red-300',
  rechazado: 'bg-red-100 text-red-700 border-red-300',
};

const STATUS_LABELS = {
  pending: 'Pendiente', encamino: 'En Camino', entregado: 'Entregado',
  cancelado: 'Cancelado', rechazado: 'Rechazado',
};

export function ExpressDeliveryModal({ delivery, onClose }) {
  const mapRef      = useRef(null);
  const mapInstance = useRef(null);
  const routeRef    = useRef(null);
  const markersRef  = useRef([]);

  useEffect(() => {
    if (!delivery?.route_polyline || !window.google || !mapRef.current) return;

    if (!mapInstance.current) {
      mapInstance.current = new window.google.maps.Map(mapRef.current, {
        disableDefaultUI: true,
        zoomControl: true,
        styles: [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }],
      });
    }

    // Clear previous
    if (routeRef.current) routeRef.current.setMap(null);
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    const path = window.google.maps.geometry.encoding.decodePath(delivery.route_polyline);

    routeRef.current = new window.google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: '#67A43C',
      strokeOpacity: 0.9,
      strokeWeight: 5,
    });
    routeRef.current.setMap(mapInstance.current);

    const bounds = new window.google.maps.LatLngBounds();
    path.forEach(p => bounds.extend(p));
    mapInstance.current.fitBounds(bounds, 40);

    // Pickup marker
    markersRef.current.push(new window.google.maps.Marker({
      position: { lat: delivery.pickup_lat, lng: delivery.pickup_lng },
      map: mapInstance.current,
      icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 9, fillColor: '#B1336E', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 },
    }));

    // Dropoff marker
    markersRef.current.push(new window.google.maps.Marker({
      position: { lat: delivery.dropoff_lat, lng: delivery.dropoff_lng },
      map: mapInstance.current,
      icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 9, fillColor: '#67A43C', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 },
    }));
  }, [delivery]);

  if (!delivery) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-brand-green/10 flex items-center justify-center">
              <Zap className="w-4 h-4 text-brand-green" />
            </div>
            <div>
              <h2 className="text-base font-extrabold text-brand-green font-montserrat italic">
                Delivery Express
              </h2>
              <p className="text-xs text-slate-500 font-montserrat">ID: {delivery.id}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {/* Status + vehicle */}
          <div className="flex items-center gap-3 justify-center">
            <span className={`px-4 py-1.5 text-sm font-bold rounded-xl border-2 font-montserrat ${STATUS_STYLES[delivery.status] || 'bg-slate-100 text-slate-700 border-slate-300'}`}>
              {STATUS_LABELS[delivery.status] || delivery.status}
            </span>
            <span className={`px-3 py-1.5 text-sm font-bold rounded-xl text-white font-montserrat ${delivery.vehicle_type === 'bike' ? 'bg-brand-green' : 'bg-brand-primary'}`}>
              {delivery.vehicle_type === 'bike' ? '🏍 Moto' : '🚗 Auto'}
            </span>
          </div>

          {/* Mini map with polyline */}
          <div
            ref={mapRef}
            className="w-full h-48 rounded-xl overflow-hidden border-2 border-slate-200"
          />

          {/* Price + distance */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-brand-green/5 rounded-xl p-3 border border-brand-green/20">
              <div className="flex items-center gap-1.5 mb-1">
                <DollarSign className="w-3.5 h-3.5 text-brand-green" />
                <span className="text-xs font-semibold text-slate-500 font-montserrat uppercase">Total</span>
              </div>
              <p className="text-xl font-extrabold text-brand-green font-montserrat">
                S/ {Number(delivery.total_price).toFixed(2)}
              </p>
              <p className="text-xs text-slate-400 font-montserrat">
                S/ {Number(delivery.rate_per_km).toFixed(2)}/km
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
              <div className="flex items-center gap-1.5 mb-1">
                <Zap className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-xs font-semibold text-slate-500 font-montserrat uppercase">Distancia</span>
              </div>
              <p className="text-xl font-extrabold text-slate-800 font-montserrat">
                {delivery.distance_km} km
              </p>
            </div>
          </div>

          {/* Recipient */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <User className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-xs font-semibold text-slate-500 font-montserrat uppercase">Destinatario</span>
              </div>
              <p className="text-sm font-semibold text-slate-900 font-montserrat">{delivery.recipient_name}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Phone className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-xs font-semibold text-slate-500 font-montserrat uppercase">Teléfono</span>
              </div>
              <p className="text-sm font-semibold text-slate-900 font-montserrat">{delivery.recipient_phone}</p>
            </div>
          </div>

          {/* Pickup */}
          <div className="bg-slate-50 rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <MapPin className="w-3.5 h-3.5 text-brand-primary" />
              <span className="text-xs font-semibold text-slate-500 font-montserrat uppercase">Recojo</span>
            </div>
            <p className="text-sm text-slate-700 font-montserrat">{delivery.pickup_address}</p>
          </div>

          {/* Dropoff */}
          <div className="bg-slate-50 rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Navigation className="w-3.5 h-3.5 text-brand-green" />
              <span className="text-xs font-semibold text-slate-500 font-montserrat uppercase">Destino</span>
            </div>
            <p className="text-sm text-slate-700 font-montserrat">{delivery.dropoff_address}</p>
          </div>

          {/* Products */}
          {delivery.items && delivery.items.length > 0 && (
            <div className="bg-slate-50 rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <Package className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-xs font-semibold text-slate-500 font-montserrat uppercase">Productos</span>
              </div>
              <div className="space-y-1">
                {delivery.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-xs font-montserrat text-slate-600">
                    <span>{item.product_sku}</span>
                    <span className="font-semibold">x{item.quantity}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {delivery.notes && (
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-xs font-semibold text-slate-500 font-montserrat uppercase mb-1">Notas</p>
              <p className="text-sm text-slate-700 font-montserrat">{delivery.notes}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200">
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-brand-green hover:bg-brand-green/90 text-white font-semibold rounded-xl transition-colors font-montserrat"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}